import { create } from 'zustand';
import type {
  AgentSummary,
  AgentConversationSummary,
  AgentConversationDetail,
  AgentFilters,
  FilterOption,
} from '../types';
import {
  getAgentSummary,
  getAgentConversations,
  getAgentConversation,
  getMetricsOrganizations,
  getMetricsUsers,
} from '../api/dashboard';

const defaultEnd = new Date();
const defaultStart = new Date();
defaultStart.setDate(defaultStart.getDate() - 7);

const PAGE_SIZE = 50;

// Guards the single-transcript slot against out-of-order responses: bumped on
// every load and on clear, so a slow request can neither overwrite a newer one
// nor repopulate a panel the user already closed. Transcripts are now opened by
// URL, so back/forward makes overlapping loads easy to trigger.
let conversationRequestToken = 0;

interface AgentState {
  // Overview
  summary: AgentSummary | null;
  isSummaryLoading: boolean;

  // Conversations
  conversations: AgentConversationSummary[];
  conversationsTotal: number;
  isConversationsLoading: boolean;
  page: number;

  // Selected transcript
  selectedConversation: AgentConversationDetail | null;
  isConversationLoading: boolean;
  conversationError: string | null;

  // Filters (shared across both views)
  dateRange: { start: Date; end: Date };
  surfaces: string[];
  agentId: string | null;
  orgIds: string[];
  userIds: string[];
  search: string;

  // Internal/test-org exclusions (sourced from the shared exclusions store)
  excludeOrgIds: string[];
  excludeUserIds: string[];

  // Org/user filter options
  availableOrgs: FilterOption[];
  availableUsers: FilterOption[];
  isOptionsLoaded: boolean;

  error: string | null;

  // Actions
  setDateRange: (start: Date, end: Date) => void;
  setSurfaces: (surfaces: string[]) => void;
  setAgentId: (agentId: string | null) => void;
  setOrgIds: (orgIds: string[]) => void;
  setUserIds: (userIds: string[]) => void;
  setExclusions: (excludeOrgIds: string[], excludeUserIds: string[]) => void;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;

  loadFilterOptions: () => Promise<void>;
  loadSummary: () => Promise<void>;
  loadConversations: () => Promise<void>;
  loadConversation: (threadId: string) => Promise<void>;
  clearSelectedConversation: () => void;
}

function currentFilters(state: AgentState): AgentFilters {
  return {
    startDate: state.dateRange.start,
    endDate: state.dateRange.end,
    surfaces: state.surfaces.length > 0 ? state.surfaces : undefined,
    agentId: state.agentId ?? undefined,
    orgIds: state.orgIds.length > 0 ? state.orgIds : undefined,
    userIds: state.userIds.length > 0 ? state.userIds : undefined,
    excludeOrgIds: state.excludeOrgIds.length > 0 ? state.excludeOrgIds : undefined,
    excludeUserIds: state.excludeUserIds.length > 0 ? state.excludeUserIds : undefined,
    search: state.search || undefined,
  };
}

export const useAgentStore = create<AgentState>((set, get) => ({
  summary: null,
  isSummaryLoading: false,
  conversations: [],
  conversationsTotal: 0,
  isConversationsLoading: false,
  page: 1,
  selectedConversation: null,
  isConversationLoading: false,
  conversationError: null,
  dateRange: { start: defaultStart, end: defaultEnd },
  surfaces: [],
  agentId: null,
  orgIds: [],
  userIds: [],
  search: '',
  excludeOrgIds: [],
  excludeUserIds: [],
  availableOrgs: [],
  availableUsers: [],
  isOptionsLoaded: false,
  error: null,

  setDateRange: (start, end) => set({ dateRange: { start, end }, page: 1 }),
  setSurfaces: (surfaces) => set({ surfaces, page: 1 }),
  setAgentId: (agentId) => set({ agentId, page: 1 }),
  setOrgIds: (orgIds) => set({ orgIds, page: 1 }),
  setUserIds: (userIds) => set({ userIds, page: 1 }),
  setExclusions: (excludeOrgIds, excludeUserIds) =>
    set({ excludeOrgIds, excludeUserIds, page: 1 }),
  setSearch: (search) => set({ search, page: 1 }),
  setPage: (page) => set({ page }),

  loadFilterOptions: async () => {
    if (get().isOptionsLoaded) return;
    try {
      const [orgs, users] = await Promise.all([getMetricsOrganizations(), getMetricsUsers()]);
      set({ availableOrgs: orgs, availableUsers: users, isOptionsLoaded: true });
    } catch {
      // non-fatal — filters just won't have options
    }
  },

  loadSummary: async () => {
    set({ isSummaryLoading: true, error: null });
    try {
      const summary = await getAgentSummary(currentFilters(get()));
      set({ summary, isSummaryLoading: false });
    } catch (e) {
      set({
        isSummaryLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load agent summary',
      });
    }
  },

  loadConversations: async () => {
    const state = get();
    set({ isConversationsLoading: true, error: null });
    try {
      const res = await getAgentConversations(currentFilters(state), state.page, PAGE_SIZE);
      set({
        conversations: res.conversations,
        conversationsTotal: res.total,
        isConversationsLoading: false,
      });
    } catch (e) {
      set({
        isConversationsLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load conversations',
      });
    }
  },

  loadConversation: async (threadId) => {
    const token = ++conversationRequestToken;
    set({ isConversationLoading: true, conversationError: null, selectedConversation: null });
    try {
      const detail = await getAgentConversation(threadId);
      if (token !== conversationRequestToken) return;
      set({ selectedConversation: detail, isConversationLoading: false });
    } catch (e) {
      if (token !== conversationRequestToken) return;
      set({
        isConversationLoading: false,
        conversationError: e instanceof Error ? e.message : 'Failed to load conversation',
      });
    }
  },

  clearSelectedConversation: () => {
    conversationRequestToken++;
    set({ selectedConversation: null, conversationError: null, isConversationLoading: false });
  },
}));

export { PAGE_SIZE };
